import { Icon as IconUikit, ICONS, palette } from '@bagonboard/ui-kit';
import {
  Drawer,
  Hidden,
  Icon,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
} from '@material-ui/core';
import { OpenInNew as OpenInNewIcon } from '@material-ui/icons';
import cx from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { useSidebarStyle } from './sidebarStyle';

const BG_COLORS = Object.freeze({
  white: 'white',
  black: 'black',
  blue : 'blue',
});

const COLORS = Object.freeze({
  white : 'white',
  red   : 'red',
  orange: 'orange',
  green : 'green',
  blue  : 'blue',
  purple: 'purple',
  rose  : 'rose',
});

const Sidebar = ({
  bgColor,
  color,
  handleDrawerToggle,
  open,
  routes,
}) => {
  const location = useLocation();
  const classes = useSidebarStyle();

  const logoClasses = cx(classes.logo, {
    [classes.whiteAfter]: bgColor === BG_COLORS.white,
  });

  const links = useMemo(() => (
    <List className={classes.list}>
      {routes.map((prop) => {
        if (prop.redirect || prop.hidden) {
          return null;
        }

        const properPath = prop.path.replace(/\/:\w+\??/g, '');
        const navLinkClasses = cx(classes.itemLink, {
          [classes[color]]: location.pathname.indexOf(properPath) > -1,
        });

        const listItemClasses = separator => cx(
          classes.item,
          separator ? classes.whiteBeforeLink : {}
        );

        const linkComponent = (params, children) => (
          params.externalLink
            ? (
              <Link className={navLinkClasses} href={params.externalLink} target={params.onNewTab ? '_blank' : ''}>
                {children}
              </Link>
            )
            : (
              <NavLink to={properPath} className={navLinkClasses} data-testid={`sidebar-item-${properPath}`}>
                {children}
              </NavLink>
            )
        );

        return (
          <ListItem key={prop.path} className={listItemClasses(prop.separator)}>
            {linkComponent(prop, (
              <>
                <ListItemIcon className={classes.itemIcon}>
                  {typeof prop.icon === 'string' ? <Icon>{prop.icon}</Icon> : <prop.icon />}
                </ListItemIcon>
                <ListItemText primary={prop.name} disableTypography className={classes.itemText} />
                {prop.externalLink && (
                  <ListItemIcon className={classes.itemIcon}>
                    <OpenInNewIcon />
                  </ListItemIcon>
                )}
              </>
            ))}
          </ListItem>
        );
      })}
    </List>
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [JSON.stringify(routes), location.pathname, color]);


  const brand = (
    <div className={logoClasses}>
      <IconUikit color={palette.primary} icon={ICONS.BOB} size={100} />
    </div>
  );


  return (
    <div>
      <Hidden mdUp implementation="css">
        <Drawer
          variant="temporary"
          anchor="right"
          open={open}
          classes={{
            paper: `${classes.drawerPaper} ${classes[`${bgColor}Background`]}`,
          }}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
        >
          {brand}
          <div className={classes.sidebarWrapper}>
            <div role="button" tabIndex={0} onClick={handleDrawerToggle} aria-hidden="true">
              {links}
            </div>
          </div>
        </Drawer>
      </Hidden>
      <Hidden smDown implementation="css">
        <Drawer
          anchor="left"
          variant="permanent"
          open
          classes={{
            paper: `${classes.drawerPaper} ${classes[`${bgColor}Background`]}`,
          }}
        >
          {brand}
          <div className={classes.sidebarWrapper}>{links}</div>
        </Drawer>
      </Hidden>
    </div>
  );
};

Sidebar.propTypes = {
  bgColor           : PropTypes.oneOf(Object.values(BG_COLORS)),
  color             : PropTypes.oneOf(Object.values(COLORS)),
  handleDrawerToggle: PropTypes.func,
  open              : PropTypes.bool,
  routes            : PropTypes.arrayOf(PropTypes.shape({})),
};

Sidebar.defaultProps = {
  bgColor: BG_COLORS.white,
  color  : COLORS.blue,
  handleDrawerToggle() {},
  open   : false,
  routes : [],
};

export default Sidebar;
